import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '@/stores/authStore';
import { progressService } from '@/services/progress';
import { realtimeService } from '@/services/realtime';

interface AuthLoadingState {
  step: 'auth' | 'profile' | 'progress' | 'ready' | 'complete';
  progress: number;
  isInitialLoad: boolean;
  canProceed: boolean; // Can show app even if some steps are still loading
}

export const useOptimizedAuth = () => {
  const { isAuthenticated, isLoading: storeLoading, user } = useAuthStore(
    useShallow(state => ({
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      user: state.user
    }))
  );
  const [loadingState, setLoadingState] = useState<AuthLoadingState>({
    step: 'auth',
    progress: 0,
    isInitialLoad: true,
    canProceed: false
  });

  useEffect(() => {
    let cancelled = false;

    if (!storeLoading && isAuthenticated && user) {
      // User is authenticated, start background loading with proper timing
      setTimeout(() => {
        if (!cancelled) handleAuthenticatedUser();
      }, 100); // Small delay to ensure loading screen shows
    } else if (!storeLoading && !isAuthenticated) {
      // Not authenticated, can proceed to login
      if (!cancelled) {
        setLoadingState({
          step: 'complete',
          progress: 100,
          isInitialLoad: false,
          canProceed: true
        });
      }
    }

    return () => { cancelled = true; };
  }, [isAuthenticated, storeLoading, user]);

  const handleAuthenticatedUser = async () => {
    setLoadingState(prev => ({ 
      ...prev, 
      step: 'profile', 
      progress: 25,
      canProceed: true // Allow immediate navigation after auth
    }));

    try {
      // Critical: Ensure user data is in store (already should be from auth)
      if (user) {
        setLoadingState(prev => ({ 
          ...prev, 
          step: 'progress', 
          progress: 50 
        }));

        // Parallelize non-critical operations for faster initialization
        const backgroundTasks = Promise.allSettled([
          // Fetch user progress data
          progressService.fetchUserProgress().then(() => {
            console.log('Progress data synced successfully');
          }),
          
          // Initialize real-time subscriptions
          realtimeService.initializeRealtime().then(() => {
            console.log('Real-time subscriptions initialized');
          }),
          
          // Minimal delay removed for faster performance
          Promise.resolve()
        ]);

        // Update loading state while background tasks run
        setLoadingState(prev => ({ 
          ...prev, 
          step: 'ready', 
          progress: 75 
        }));

        // Wait for all background tasks to complete or fail
        const results = await backgroundTasks;
        
        // Log any failures but don't block the app
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const taskNames = ['Progress sync', 'Real-time init', 'UI delay'];
            console.warn(`${taskNames[index]} failed:`, result.reason);
          }
        });

        // Mark as complete regardless of individual task outcomes
        setLoadingState(prev => ({ 
          ...prev, 
          step: 'complete', 
          progress: 100,
          isInitialLoad: false
        }));
      }
    } catch (error) {
      console.error('Error in optimized auth flow:', error);
      // Even on error, allow the app to proceed
      setLoadingState(prev => ({ 
        ...prev, 
        step: 'complete', 
        progress: 100,
        isInitialLoad: false,
        canProceed: true
      }));
    }
  };

  const result = {
    isLoading: loadingState.isInitialLoad || storeLoading,
    canProceed: loadingState.canProceed && !storeLoading, // Don't allow proceeding while store is still loading
    loadingStep: loadingState.step,
    progress: loadingState.progress,
    isAuthenticated,
    user
  };

  return result;
};